import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { 
  ArrowRight, 
  Package, 
  FileText, 
  Users, 
  BarChart3,
  CreditCard,
  CheckCircle,
  Zap,
  Shield,
  Clock,
  Globe,
  ShoppingCart,
  Droplets,
  Truck,
  Wrench,
  Eye,
  Mail,
  Lock,
  Loader2,
  Sun,
  Moon,
  Smartphone,
  ChevronDown,
  Plug
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import PlatformChatBot from "@/components/PlatformChatBot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Logo Component
const BusinessPortalLogo = ({ size = 40 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <linearGradient id="logoGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#60A5FA" />
        <stop offset="100%" stopColor="#818CF8" />
      </linearGradient>
      <linearGradient id="logoGrad2" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#A78BFA" />
        <stop offset="100%" stopColor="#C084FC" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="4" fill="url(#logoGrad1)" />
    <rect x="26" y="2" width="20" height="20" rx="4" fill="url(#logoGrad2)" opacity="0.9" />
    <rect x="2" y="26" width="20" height="20" rx="4" fill="url(#logoGrad2)" opacity="0.9" />
    <rect x="26" y="26" width="20" height="20" rx="4" fill="url(#logoGrad1)" />
  </svg>
);

export default function Landing() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  const darkMode = theme === "dark";

  useEffect(() => { setMounted(true); }, []);

  const categories = [
    { icon: Wrench, name: "Mechanic Shops", description: "Auto repair, truck repair, service centers", features: ["Invoicing", "Parts Inventory", "Customer Management"], color: "blue", link: "/demos?play=mechanic" },
    { icon: Globe, name: "Wholesale / Import", description: "Import/export, wholesale distribution", features: ["Purchase Orders", "Shipment Tracking", "Landed Cost"], color: "purple", link: "/demos?play=wholesale" },
    { icon: ShoppingCart, name: "Markets & Convenience", description: "Grocery, mini-marts, bodegas, supermarkets, convenience stores", features: ["POS / Clover Sync", "EBT & Tax Tracking", "Expiry Alerts"], color: "emerald", link: "/demos?play=grocery" },
    { icon: Droplets, name: "Pressure Washing", description: "Residential + commercial exterior cleaning (driveways, patios, decks, roofs)", features: ["Aerial Map Quotes", "Auto Sqft Calc", "Per-Surface Rates"], color: "cyan", link: "/demo/pressure_washing", badge: "NEW" }
  ];

  const features = [
    { icon: Package, title: "Live Inventory", desc: "Real-time stock tracking" },
    { icon: FileText, title: "Professional Invoices", desc: "Create & send in seconds" },
    { icon: Users, title: "Customer Management", desc: "All contacts in one place" },
    { icon: CreditCard, title: "Easy Payments", desc: "Cards, Zelle, Square" },
    { icon: BarChart3, title: "Business Insights", desc: "Revenue & analytics" },
    { icon: Shield, title: "Secure & Reliable", desc: "Your data is safe" }
  ];

  // Detect Clover-referred visitors so we can show a tailored hero CTA
  const fromClover = typeof window !== 'undefined' && (
    window.location.search.includes('utm_source=clover') ||
    window.location.search.includes('ref=clover') ||
    document.referrer.includes('clover.com') ||
    document.referrer.includes('apps.clover.com')
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Please enter email and password"); return; }
    setLoading(true);
    // 1-shot retry to absorb the Cloudflare __cf_bm cold-start that
    // bites the first request and clears on the second.
    const attemptLogin = async (allowRetry = true) => {
      try {
        return await axios.post(`${API}/auth/login`, {
          email: email.toLowerCase().trim(),
          password,
        }, { timeout: 12000 });
      } catch (err) {
        if (!err.response && allowRetry) {
          await new Promise(r => setTimeout(r, 400));
          return attemptLogin(false);
        }
        throw err;
      }
    };
    try {
      const response = await attemptLogin(true);

      // Clear any old cached data first
      localStorage.removeItem("app_user");

      // BELT-AND-BRACES: persist the JWT + set axios Authorization
      // BEFORE we navigate, so any auth-required call on the next
      // page already carries the Bearer header. AuthContext.login()
      // also does this but a race (testing iter_34) showed it can
      // come up empty.
      if (response.data?.access_token) {
        try {
          localStorage.setItem("app_token", response.data.access_token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${response.data.access_token}`;
        } catch { /* private mode / quota — non-fatal */ }
      }

      login({
        id: response.data.user_id, name: response.data.name, email: response.data.email,
        phone: response.data.phone, role: response.data.role, business_id: response.data.business_id,
        business_slug: response.data.business_slug, business_name: response.data.business_name,
        business_category: response.data.business_category,
        // CRITICAL: pass the token through so AuthContext.login persists it
        // and sets the global axios Authorization header.
        access_token: response.data.access_token,
      });
      toast.success(`Welcome back, ${response.data.name}!`);
      
      // Determine the target URL
      let targetUrl;
      if (response.data.role === "super_admin") {
        targetUrl = "/admin";
      } else if (response.data.business_slug) {
        const category = response.data.business_category;
        if (category === "grocery_store") {
          targetUrl = `/${response.data.business_slug}/grocery`;
        } else if (category === "wholesale") {
          targetUrl = `/${response.data.business_slug}/wholesale`;
        } else {
          targetUrl = `/${response.data.business_slug}`;
        }
      } else {
        toast.error("No business associated with this account");
        return;
      }
      
      // Force a full page reload to clear any cached state
      window.location.href = targetUrl;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or password");
    } finally { setLoading(false); }
  };

  const getColorClasses = (color) => ({
    blue: { bg: "bg-blue-500/15", text: "text-blue-400", border: "border-blue-500/30" },
    purple: { bg: "bg-purple-500/15", text: "text-purple-400", border: "border-purple-500/30" },
    emerald: { bg: "bg-emerald-500/15", text: "text-emerald-400", border: "border-emerald-500/30" },
    cyan: { bg: "bg-cyan-500/15", text: "text-cyan-400", border: "border-cyan-500/30" },
    amber: { bg: "bg-amber-500/15", text: "text-amber-400", border: "border-amber-500/30" }
  }[color] || { bg: "bg-slate-500/15", text: "text-slate-400", border: "border-slate-500/30" });

  if (!mounted) return <div className="min-h-screen bg-slate-950" />;

  // Inline sign-in form JSX to prevent re-render focus loss
  const signInFormJSX = (
    <div className={`rounded-2xl border backdrop-blur-sm ${darkMode ? 'bg-slate-900/95 border-slate-700' : 'bg-white border-gray-200 shadow-2xl'}`}>
      <div className="p-6">
        <div className="text-center mb-5">
          <h2 className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Sign In</h2>
          <p className={`text-sm mt-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Access your dashboard</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-3">
          <div>
            <Label className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Email</Label>
            <div className="relative mt-1">
              <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input type="email" placeholder="you@business.com" value={email} onChange={(e) => setEmail(e.target.value)}
                className={`pl-10 h-10 ${darkMode ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-300'}`} data-testid="email-input" />
            </div>
          </div>
          <div>
            <Label className={`text-sm ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>Password</Label>
            <div className="relative mt-1">
              <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} />
              <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)}
                className={`pl-10 h-10 ${darkMode ? 'bg-slate-800 border-slate-600 text-white placeholder:text-slate-500' : 'bg-gray-50 border-gray-300'}`} data-testid="password-input" />
            </div>
          </div>
          <Button type="submit" disabled={loading} className="w-full h-10 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white font-semibold" data-testid="login-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Sign In <ArrowRight className="w-4 h-4 ml-2" /></>}
          </Button>
          <p className="text-center">
            <Link to="/forgot-password" className={`text-xs ${darkMode ? 'text-slate-400 hover:text-slate-300' : 'text-slate-500 hover:text-slate-700'}`}>Forgot password?</Link>
          </p>
        </form>
        <div className={`mt-4 pt-4 border-t ${darkMode ? 'border-slate-700' : 'border-gray-200'}`}>
          <Link to="/signup">
            <Button variant="outline" size="sm" className={`w-full font-semibold ${darkMode ? 'border-purple-500/50 text-purple-400 hover:bg-purple-500/10' : 'border-purple-500 text-purple-600 hover:bg-purple-50'}`}>
              Create Free Account
            </Button>
          </Link>
          <p className={`text-xs text-center mt-2 ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>No credit card required</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-950' : 'bg-gray-50'}`} data-testid="landing-page">
      
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b ${darkMode ? 'bg-slate-950/90 border-slate-800' : 'bg-white/90 border-gray-200'}`}>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 xl:px-20">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <BusinessPortalLogo size={32} />
              <span className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-slate-900'}`}>My Business Portal</span>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/demos" className="hidden sm:block">
                <Button variant="ghost" size="sm" className={darkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-900'}>
                  <Eye className="w-4 h-4 mr-1" /> Demos
                </Button>
              </Link>
              <button onClick={toggleTheme} className={`p-2 rounded-full ${darkMode ? 'text-yellow-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-gray-100'}`}>
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <Link to="/signup" className="hidden sm:block">
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-20 pb-6 lg:pt-24 lg:pb-8 px-6 lg:px-12 xl:px-20">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid lg:grid-cols-[1fr,340px] gap-8 lg:gap-16 items-start">
            
            {/* Left - Hero Content */}
            <div>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm mb-6 ${
                fromClover
                  ? (darkMode ? 'bg-cyan-500/10 border border-cyan-500/30 text-cyan-400' : 'bg-cyan-50 border border-cyan-200 text-cyan-700')
                  : (darkMode ? 'bg-purple-500/10 border border-purple-500/20 text-purple-400' : 'bg-purple-50 border border-purple-200 text-purple-600')
              }`}>
                {fromClover
                  ? <><Plug className="w-4 h-4" /> Welcome from Clover · One-tap install</>
                  : <><Zap className="w-4 h-4" /> Built for your business</>}
              </div>
              
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                {fromClover ? (
                  <>Sync your{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-blue-600">
                      Clover register
                    </span>{' '}
                    in 2 minutes
                  </>
                ) : (
                  <>One Platform for{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500">
                      Every Business Type
                    </span>
                  </>
                )}
              </h1>
              
              <p className={`text-base sm:text-lg mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {fromClover
                  ? <>Pull live sales, tax breakdowns, EBT &amp; cash splits straight from your Clover register into a clean dashboard. <span className="font-semibold">Free for now</span> — no per-transaction fees, ever.</>
                  : <>Invoices, inventory, and customer management — tailored for your industry. Stop juggling spreadsheets. Focus on your business.</>}
              </p>

              {/* Selling Points */}
              <div className={`grid grid-cols-2 gap-3 mb-6 ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                <div className={`flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium">{fromClover ? "Sales by hour, day, week" : "Create invoices in 60 seconds"}</span>
                </div>
                <div className={`flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium">{fromClover ? "Tax-filing-ready exports" : "Accept cards, Zelle & Apple Pay"}</span>
                </div>
                <div className={`flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium">{fromClover ? "EBT vs cash vs card splits" : "Auto-track inventory & costs"}</span>
                </div>
                <div className={`flex items-center gap-2 p-3 rounded-lg ${darkMode ? 'bg-slate-800/50' : 'bg-gray-100'}`}>
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-sm font-medium">{fromClover ? "Real-time low-stock alerts" : "Email invoices with 1 click"}</span>
                </div>
              </div>

              {/* Trust Badges */}
              <div className={`flex flex-wrap gap-4 mb-8 text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-green-500" /> Free to start</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-blue-500" /> Setup in 5 min</span>
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-purple-500" /> Secure & reliable</span>
              </div>

              {/* Mobile CTA */}
              <div className="flex gap-3 lg:hidden mb-8">
                <a href="#signin" className="flex-1">
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white">Sign In</Button>
                </a>
                <Link to="/signup" className="flex-1">
                  <Button variant="outline" className={`w-full ${darkMode ? 'border-slate-600 text-slate-300' : 'border-gray-300'}`}>Get Started</Button>
                </Link>
              </div>
            </div>

            {/* Right - Sign In Form (Desktop) */}
            <div className="hidden lg:block">
              {signInFormJSX}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className={`py-8 lg:py-10 px-6 lg:px-12 xl:px-20 ${darkMode ? 'bg-slate-900/50' : 'bg-white'}`}>
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-6">
            <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Built for Your Industry</h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Choose your business type and get a solution tailored to your needs</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {categories.map((cat, idx) => {
              const colors = getColorClasses(cat.color);
              const Wrapper = cat.link ? Link : 'div';
              const wrapperProps = cat.link ? { to: cat.link } : {};
              
              return (
                <Wrapper key={idx} {...wrapperProps} className={cat.link ? 'block' : ''} data-testid={`landing-category-${cat.name.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                  <Card className={`h-full transition-all ${cat.link ? 'hover:scale-[1.02] cursor-pointer' : ''} ${darkMode ? 'bg-slate-800/50 border-slate-700 hover:border-slate-600' : 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:shadow-md'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`p-2 rounded-lg ${colors.bg}`}>
                          <cat.icon className={`w-5 h-5 ${colors.text}`} />
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${cat.badge ? (darkMode ? 'bg-cyan-500/20 text-cyan-200 border border-cyan-400/40 animate-pulse' : 'bg-cyan-100 text-cyan-800 border border-cyan-300 animate-pulse') : (darkMode ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30' : 'bg-cyan-50 text-cyan-700 border border-cyan-200')}`}>
                          {cat.badge ? <><Droplets className="w-3 h-3" /> {cat.badge}</> : <><Eye className="w-3 h-3" /> View demo</>}
                        </span>
                      </div>
                      <h3 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{cat.name}</h3>
                      <p className={`text-sm mb-3 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{cat.description}</p>
                      <div className="flex flex-wrap gap-1">
                        {cat.features.slice(0, 2).map((f, i) => (
                          <span key={i} className={`text-xs px-2 py-0.5 rounded ${darkMode ? 'bg-slate-700/50 text-slate-300' : 'bg-gray-200 text-slate-600'}`}>{f}</span>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </Wrapper>
              );
            })}
          </div>

          <div className="text-center mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/demos">
              <Button variant="outline" size="sm" className={`${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-gray-300 text-slate-600 hover:bg-gray-100'}`}>
                <Eye className="w-4 h-4 mr-2" /> Explore All Demos
              </Button>
            </Link>
            <Link to="/demo/play/grocery">
              <Button size="sm" className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:opacity-90 text-white" data-testid="watch-interactive-demo-btn">
                ▶ Watch interactive demo (90 sec)
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-8 lg:py-10 px-6 lg:px-12 xl:px-20">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-6">
            <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Everything You Need</h2>
            <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>All the tools to run your business efficiently</p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {features.map((f, idx) => (
              <div key={idx} className={`text-center p-4 rounded-xl ${darkMode ? 'bg-slate-800/30 border border-slate-700/50' : 'bg-white border border-gray-200 shadow-sm'}`}>
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${darkMode ? 'bg-purple-500/10' : 'bg-purple-50'}`}>
                  <f.icon className={`w-6 h-6 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <h4 className={`font-medium text-sm mb-1 ${darkMode ? 'text-white' : 'text-slate-900'}`}>{f.title}</h4>
                <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mobile Sign In Section */}
      <section id="signin" className="lg:hidden py-12 px-4 sm:px-6">
        <div className="max-w-md mx-auto">
          {signInFormJSX}
        </div>
      </section>

      {/* CTA Section */}
      <section className={`py-12 lg:py-16 px-6 lg:px-12 xl:px-20 ${darkMode ? 'bg-slate-900/50' : 'bg-gray-100'}`}>
        <div className="max-w-4xl mx-auto text-center">
          <div className={`p-8 sm:p-10 rounded-2xl ${darkMode ? 'bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/20' : 'bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200'}`}>
            <h2 className={`text-2xl sm:text-3xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Ready to streamline your business?</h2>
            <p className={`mb-6 ${darkMode ? 'text-slate-400' : 'text-slate-600'}`}>Join hundreds of businesses already using My Business Portal</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/signup">
                <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-white px-8">Create Free Account</Button>
              </Link>
              <Link to="/demos">
                <Button size="lg" variant="outline" className={`${darkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-800' : 'border-gray-300 text-slate-700 hover:bg-white'}`}>
                  <Eye className="w-4 h-4 mr-2" /> View Demos
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={`py-8 px-6 lg:px-12 xl:px-20 border-t ${darkMode ? 'bg-slate-950 border-slate-800' : 'bg-white border-gray-200'}`}>
        <div className="max-w-[1600px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BusinessPortalLogo size={24} />
            <span className={`text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>© 2025 My Business Portal</span>
          </div>
          <div className={`flex items-center gap-4 text-sm ${darkMode ? 'text-slate-500' : 'text-slate-500'}`}>
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <Link to="/terms" className="hover:underline">Terms</Link>
            <Link to="/demos" className="hover:underline">Demos</Link>
            <Link to="/support" className="hover:underline">Support</Link>
          </div>
        </div>
      </footer>

      <PlatformChatBot />
    </div>
  );
}
